/*
 * © 2020 Microsoft. All rights reserved.
 */

define('Managers/TelemetryManager',['absolute/storagemanager'], function (StorageManager) {
    "use strict";

    var TelemetryManager = {

        _installId: null,
        _key: "InstallId",
        _appInsights: null,

        setInstallId: function () {
            if (StorageManager.prefix) {
                if (!this._installId) {
                    this._installId = StorageManager.fetch(this._key);
                }
            }
        },

        appException: function (error) {
            // Call through ExceptionManager to throttle, do not call this directly.
            this.setInstallId();
            
            console.info('ApplicationInsightsException: ', error);
            
            var errorProperties = {
                gameURL: window.location.href,
                installId: this._installId
            };

            this._appInsights.trackException({ exception: error, severityLevel: "Error", properties: errorProperties });
        },

        appLaunch: function (AppInsights) {
            this._appInsights = AppInsights;
            this._trackEvent('AppLaunch');
        },

        challengeIsNull: function (monthYearStr, AdditionalDataNeeded, Coins, Completed, Date, Difficulty, Key) {
            this._trackEvent('ChallengeIsNull', {
                monthYearStr: monthYearStr,
                configAdditionalDataNeeded: AdditionalDataNeeded,
                configCoins: Coins,
                configCompleted: Completed,
                configDate: Date,
                configDifficulty: Difficulty,
                configKey: Key
            });
        },

        badgeAwarded: function (badge, month, year, bronzeBadges, silverBadges, goldBadges, diamondBadges,
            perfectBadges, klondikeChallenges, spiderChallenges, freecellChallenges,
            pyramidChallenges, tripeaksChallenges, easyChallenges, mediumChallenges,
            hardChallenges, expertChallenges) {
            this._trackEvent('BadgeAwarded', {
                badge: badge, month: month, year: year, bronzeBadges: bronzeBadges, silverBadges: silverBadges,
                goldBadges: goldBadges, diamondBadges: diamondBadges,
                perfectBadges: perfectBadges, klondikeChallenges: klondikeChallenges,
                spiderChallenges: spiderChallenges, freecellChallenges: freecellChallenges,
                pyramidChallenges: pyramidChallenges, tripeaksChallenges: tripeaksChallenges,
                easyChallenges: easyChallenges, mediumChallenges: mediumChallenges,
                hardChallenges: hardChallenges, expertChallenges: expertChallenges
            });
        },

        challengeComplete: function (gameResult, gameMode, timeSpent, releaseDate, difficulty) {
            this._trackEvent('ChallengeComplete', {
                gameResult: gameResult, gameMode: gameMode, timeSpent: timeSpent, releaseDate: releaseDate,
                difficulty: difficulty
            });
        },

        challengeStart: function (releaseDate, gameMode, isRestart, difficulty) {
            this._trackEvent('ChallengeStart', {
                releaseDate: releaseDate, gameMode: gameMode, source: isRestart ? 'Restart' : 'New', difficulty
            });
        },

        classicGameComplete: function (gameResult, gameMode, timeSpent, scoringOption, drawOption, numSuits, difficulty) {
            const difficultyValue = this._convertDifficulty(difficulty || "");
            this._trackEvent('ClassicComplete', {
                gameResult: gameResult,
                gameMode: gameMode,
                timeSpent: timeSpent,
                scoringOption: scoringOption || null,
                drawOption: drawOption || null,
                numSuits: numSuits || null,
                difficulty: !!difficulty && difficultyValue > -1?  difficultyValue : null
            });
        },

        classicGameStart: function (gameMode, startType, scoringOption, drawOption, numSuits, difficulty) {
            const difficultyValue = this._convertDifficulty(difficulty || "");
            this._trackEvent('ClassicStart', {
                gameMode: gameMode, startType: startType,
                scoringOption: scoringOption || null,
                drawOption: drawOption || null,
                numSuits: numSuits || null,
                difficulty: !!difficulty && difficultyValue > -1?  difficultyValue : null
            });
        },

        opennedNewDealMenu: function() {
            this._trackEvent('OpenedNewDealMenu');
        },

        closedNewDealMenu: function(selected) {
            this._trackEvent('ClosedNewDealMenu', { selection: selected? 'Started' : 'Abandoned' });
        },

        fullscreenButtonClick: function (isFullScreen) {
            isFullScreen
                ? this._trackEvent('FullscreenButtonWindowed')
                : this._trackEvent('FullscreenButtonFullscreen');
        },

        gameSettingChanged: function (settingPrefix, setting, state) {
            this._trackEvent('GameSettingChanged', {
                settingPrefix: settingPrefix,
                setting: setting,
                state: state
            });
        },

        mainMenuLoad: function (totalStarClubCompleted, totalDailyChallengeCompleted, gameModesCompleted) {
            this._trackEvent('MainMenuLoad', {
                completed: {
                    starClub: totalStarClubCompleted,
                    dailyChallenges: totalDailyChallengeCompleted,
                    gameModes: gameModesCompleted
                }
            });
        },

        manifestError: function (error) {
            this._trackEvent('AppManifest Error', {
                error: error
            });
        },

        menuOptionSelected: function (option) {
            this._trackEvent('MenuOptionSelected', {
                option: option
            });
        },

        newInstall: function () {
            this._trackEvent('NewInstall');
        },

        newsShown: function (type, reason) {
            this._trackEvent('NewsShown', {
                type: type, reason: reason
            });
        },

        saveMigrationCompleted: function (oldProgressionGamesPlayed, oldRegularGamesPlayed, newProgressionGamesPlayed, newRegularGamesPlayed) {
            this._trackEvent(
                'SaveMigrationCompleted', {
                    oldProgressionGamesPlayed: oldProgressionGamesPlayed,
                    oldRegularGamesPlayed: oldRegularGamesPlayed,
                    newProgressionGamesPlayed: newProgressionGamesPlayed,
                    newRegularGamesPlayed: newRegularGamesPlayed
                });
        },

        saveMigrationFailed: function (error, optionalLocalStorageSizes) {
            this._trackEvent('SaveMigrationFailed', { exception: error, localStorageSizes: optionalLocalStorageSizes });
            this.appException(error);
        },

        starClubComplete: function (gameResult, timeSpent, gameMode, difficulty, collectionName, packName) {
            this._trackEvent('StarClubComplete', {
                gameResult: gameResult, timeSpent: timeSpent,
                gameMode: gameMode, difficulty: difficulty,
                collectionName: collectionName,
                packName: packName
            });
        },

        starClubStart: function (gameMode) {
            this._trackEvent('StarClubStart', {
                gameMode: gameMode
            });
        },

        _trackEvent: function (event, properties) {
            if (typeof properties === "undefined") {
                properties = {
                };
            }

            this.setInstallId();
            properties.installId = this._installId;
            properties.gameURL = window.location.href;
            console.info('ApplicationInsightsEvent: ', event, properties);
            this._appInsights.trackEvent({ name: event }, properties);
        },

        _convertDifficulty: function(difficulty) {
            if (typeof difficulty !== 'string') {
                return -1;
            }
            return [
                "random",
                "easy",
                "medium",
                "hard",
                "expert",
                "master",
                "grandmaster"
            ].indexOf(difficulty.toLowerCase());
        }
    };

    return TelemetryManager;
});
